import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaTrash } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import RecipeIngredientMultiPicker from './RecipeIngredientMultiPicker';
import { ROUTES } from '../constants/appRoutes';
import {
    createFoodItemRecipe,
    updateFoodItemRecipe,
    fetchFoodItemRecipe,
    fetchRecipeIngredientOptions,
    formatRecipeIngredientLabel,
    formatRecipeIngredientName,
    formatRecipeIngredientQuantity,
    getApiErrorMessage,
    getRecipeIngredientUnit,
} from './recipeApi';
import {
    RECIPE_SERVING_COUNT,
    buildRecipeIdPayload,
    formatRecipeInstructionLines,
    getRecipeServingNoteText,
    mapRecipeDataToForm,
    recipeResponseHasContent,
    resolveRecipeFoodItemName,
} from './foodItemRecipeUtils';
import './css/Menu.css';
import './css/Inventory.css';
import './css/OrderDetails.css';
import './css/Restaurants.css';
import { confirmAction } from './ui/feedback';

function RecipeInstructionDisplay({ instruction }) {
    const lines = formatRecipeInstructionLines(instruction);
    if (!lines.length) return <span className="recipe-step-readable-line">—</span>;
    return (
        <div className="recipe-step-readable-body">
            {lines.map((line, lineIdx) => (
                <p key={`line-${lineIdx}`} className="recipe-step-readable-line">
                    {line}
                </p>
            ))}
        </div>
    );
}

const RESTAURANTS_HOME_TAB = 'restaurants';
const RECIPE_TAB_PREFIX = 'restaurants/recipe';

function recipeTabPath(foodItemId, mode = 'view', menuType, channel) {
    const suffix = mode === 'view' ? '' : `/${mode}`;
    const params = new URLSearchParams();
    params.set('tab', `${RECIPE_TAB_PREFIX}/${foodItemId}${suffix}`);
    if (menuType) params.set('menuType', menuType);
    if (channel) params.set('channel', channel);
    return `${ROUTES.businessAdmin}?${params.toString()}`;
}

function FoodItemRecipeRouteRedirect({ foodItemId }) {
    const [searchParams] = useSearchParams();
    const menuType = searchParams.get('menuType');
    return <Navigate to={recipeTabPath(foodItemId, 'view', menuType)} replace />;
}

const FoodItemRecipePageView = ({
    foodItemId,
    recipeMode = 'view',
    menuTypeFilter = 'online',
    restaurantId,
    menuItems = {},
    onBack,
    onModeChange,
    embedded = false,
}) => {
    const navigate = useNavigate();
    const [showServingNote, setShowServingNote] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [recipeResult, setRecipeResult] = useState(null);
    const [recipeInventoryItems, setRecipeInventoryItems] = useState([]);
    const [recipeIngredients, setRecipeIngredients] = useState([]);
    const [recipeStepDraft, setRecipeStepDraft] = useState({ instruction: '' });
    const [recipeSteps, setRecipeSteps] = useState([]);
    const [editingRecipeStepIndex, setEditingRecipeStepIndex] = useState(null);
    const [showRecipeIngredientPicker, setShowRecipeIngredientPicker] = useState(
        () => recipeMode === 'add'
    );
    const [showRecipeStepPicker, setShowRecipeStepPicker] = useState(false);
    const [isRecipeUpdate, setIsRecipeUpdate] = useState(false);
    const [loadError, setLoadError] = useState('');

    const menuItemId = Number(foodItemId);

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || 'null');
        } catch (e) {
            return null;
        }
    }, []);
    const createdById = Number(user?.userId || 0);
    const effectiveRestaurantId = Number(restaurantId || localStorage.getItem('restaurantId') || 0);

    const groceryOptions = useMemo(() => {
        const addedIds = new Set(
            recipeIngredients.map((it) => String(it.inventoryItemId))
        );
        return recipeInventoryItems
            .map((inv) => {
                const id = inv.inventoryItemId ?? inv.Inventory_Item_Id;
                return {
                    value: id,
                    label: formatRecipeIngredientName(inv),
                    unitSymbol: getRecipeIngredientUnit({ inventoryItemId: id }, [inv]),
                };
            })
            .filter((opt) => !addedIds.has(String(opt.value)));
    }, [recipeInventoryItems, recipeIngredients]);

    const foodName = useMemo(
        () => resolveRecipeFoodItemName(menuItems, menuItemId, recipeResult),
        [menuItems, menuItemId, recipeResult]
    );

    const recipeHasContent = recipeResult && recipeResponseHasContent(recipeResult);
    const isViewMode = recipeMode === 'view';

    const changeMode = useCallback(
        (mode) => {
            if (typeof onModeChange === 'function') {
                onModeChange(mode);
                return;
            }
            navigate(recipeTabPath(menuItemId, mode, menuTypeFilter));
        },
        [menuItemId, menuTypeFilter, navigate, onModeChange]
    );

    const handleBack = () => {
        if (typeof onBack === 'function') {
            onBack();
            return;
        }
        navigate(`${ROUTES.businessAdmin}?tab=${RESTAURANTS_HOME_TAB}`);
    };

    const resetRecipeStepEditor = () => {
        setRecipeStepDraft({ instruction: '' });
        setEditingRecipeStepIndex(null);
    };

    const resetEditForm = () => {
        setRecipeIngredients([]);
        setRecipeStepDraft({ instruction: '' });
        setRecipeSteps([]);
        setEditingRecipeStepIndex(null);
        setShowRecipeIngredientPicker(true);
        setShowRecipeStepPicker(false);
        setIsRecipeUpdate(false);
    };

    const populateEditForm = (recipeData) => {
        const { items, steps } = mapRecipeDataToForm(recipeData);
        setRecipeIngredients(items);
        setRecipeSteps(steps);
        setRecipeStepDraft({ instruction: '' });
        setEditingRecipeStepIndex(null);
        setShowRecipeIngredientPicker(false);
        setShowRecipeStepPicker(false);
        setIsRecipeUpdate(true);
    };

    const loadRecipe = useCallback(async () => {
        if (!menuItemId) {
            setLoadError('Invalid menu item.');
            setLoading(false);
            return;
        }
        if (recipeMode === 'add') {
            setLoadError('');
            setRecipeResult(null);
            resetEditForm();
            setIsRecipeUpdate(false);
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadError('');
        try {
            const data = await fetchFoodItemRecipe(buildRecipeIdPayload(menuTypeFilter, menuItemId));
            setRecipeResult(data);
            const hasContent = recipeResponseHasContent(data);

            if (recipeMode === 'edit') {
                if (!hasContent) {
                    changeMode('add');
                    return;
                }
                populateEditForm(data);
            } else if (!hasContent) {
                changeMode('add');
                return;
            }
        } catch (error) {
            setRecipeResult(null);
            if (recipeMode === 'add') {
                resetEditForm();
            } else {
                setLoadError(getApiErrorMessage(error, 'Failed to load recipe.'));
            }
        } finally {
            setLoading(false);
        }
    }, [changeMode, menuItemId, menuTypeFilter, recipeMode]);

    useEffect(() => {
        if (!effectiveRestaurantId) return;
        fetchRecipeIngredientOptions(effectiveRestaurantId)
            .then((rows) => setRecipeInventoryItems(rows))
            .catch(() => setRecipeInventoryItems([]));
    }, [effectiveRestaurantId]);

    useEffect(() => {
        loadRecipe();
    }, [loadRecipe]);

    const getRecipeInventoryItemLabel = (inventoryItemId) => {
        const row = recipeInventoryItems.find(
            (inv) =>
                String(inv.inventoryItemId ?? inv.Inventory_Item_Id) === String(inventoryItemId)
        );
        return formatRecipeIngredientLabel(row) || `Item ${inventoryItemId}`;
    };

    const getRecipeInventoryItemName = (inventoryItemId) => {
        const row = recipeInventoryItems.find(
            (inv) =>
                String(inv.inventoryItemId ?? inv.Inventory_Item_Id) === String(inventoryItemId)
        );
        return formatRecipeIngredientName(row) || `Item ${inventoryItemId}`;
    };

    const getRecipeInventoryItemUnit = (inventoryItemId) =>
        getRecipeIngredientUnit({ inventoryItemId }, recipeInventoryItems);

    const commitSelectedRecipeIngredients = (selectedItems) => {
        const items = Array.isArray(selectedItems) ? selectedItems : [];
        if (items.length === 0) return;
        const existing = new Set(recipeIngredients.map((it) => String(it.inventoryItemId)));
        const toAdd = items
            .map((item) => ({
                inventoryItemId: Number(item.inventoryItemId),
                quantity:
                    item.quantity === undefined || item.quantity === null
                        ? ''
                        : String(item.quantity),
            }))
            .filter((item) => item.inventoryItemId && !existing.has(String(item.inventoryItemId)));
        if (toAdd.length === 0) return;
        setRecipeIngredients((prev) => [...prev, ...toAdd]);
        setShowRecipeIngredientPicker(false);
    };

    const updateRecipeIngredientQuantityAt = (index, value) => {
        setRecipeIngredients((prev) =>
            prev.map((it, i) => (i === index ? { ...it, quantity: value } : it))
        );
    };

    const normalizeRecipeIngredientQuantityAt = (index) => {
        setRecipeIngredients((prev) =>
            prev.map((it, i) => {
                if (i !== index || it.quantity === '') return it;
                const qty = Number(it.quantity);
                if (Number.isNaN(qty) || qty < 0) {
                    return { ...it, quantity: '' };
                }
                return { ...it, quantity: qty };
            })
        );
    };

    const removeRecipeIngredientAt = async (index) => {
        const item = recipeIngredients[index];
        if (!item) return;
        const name = getRecipeInventoryItemLabel(item.inventoryItemId);
        const message = `Remove ingredient "${name}" from this recipe?`;
        const ok = await confirmAction({
            title: 'Remove ingredient?',
            message,
            confirmLabel: 'Remove',
            danger: true,
        });
        if (!ok) return;

        setRecipeIngredients((prev) => {
            const next = prev.filter((_, i) => i !== index);
            if (next.length === 0) setShowRecipeIngredientPicker(true);
            return next;
        });
    };

    const openRecipeStepForm = () => {
        resetRecipeStepEditor();
        setShowRecipeStepPicker(true);
    };

    const closeRecipeStepForm = () => {
        resetRecipeStepEditor();
        setShowRecipeStepPicker(false);
    };

    const startEditRecipeStepAt = (index) => {
        const step = recipeSteps[index];
        if (!step) return;
        setRecipeStepDraft({ instruction: step.instruction ?? '' });
        setEditingRecipeStepIndex(index);
        setShowRecipeStepPicker(true);
    };

    const commitRecipeStep = () => {
        const instruction = String(recipeStepDraft.instruction || '').trim();
        if (!instruction) {
            alert('Enter an instruction before adding a step.');
            return;
        }
        if (editingRecipeStepIndex !== null) {
            setRecipeSteps((prev) =>
                prev.map((st, i) =>
                    i === editingRecipeStepIndex ? { ...st, instruction } : st
                )
            );
        } else {
            setRecipeSteps((prev) => [...prev, { instruction }]);
        }
        resetRecipeStepEditor();
        setShowRecipeStepPicker(false);
    };

    const removeRecipeStepAt = async (index) => {
        const step = recipeSteps[index];
        if (!step) return;
        const preview = String(step.instruction ?? '').trim();
        const shortPreview =
            preview.length > 80 ? `${preview.slice(0, 80)}…` : preview;
        const message = shortPreview
            ? `Delete step ${index + 1}?\n\n"${shortPreview}"`
            : `Delete step ${index + 1}?`;
        const ok = await confirmAction({
            title: 'Delete step?',
            message,
            confirmLabel: 'Delete',
            danger: true,
        });
        if (!ok) return;

        setRecipeSteps((prev) => {
            const next = prev.filter((_, i) => i !== index);
            if (next.length === 0) setShowRecipeStepPicker(false);
            return next;
        });
        if (editingRecipeStepIndex === index) {
            resetRecipeStepEditor();
        } else if (editingRecipeStepIndex !== null && index < editingRecipeStepIndex) {
            setEditingRecipeStepIndex((prev) => prev - 1);
        }
    };

    const handleSaveRecipe = async (e) => {
        e.preventDefault();
        if (!menuItemId || !effectiveRestaurantId || !createdById) {
            alert('Menu item, restaurant, and user details are required.');
            return;
        }
        if (recipeIngredients.length === 0) {
            alert('Add at least one ingredient.');
            return;
        }
        const missingQuantity = recipeIngredients.some(
            (it) =>
                it.quantity === '' ||
                Number.isNaN(Number(it.quantity)) ||
                Number(it.quantity) < 0
        );
        if (missingQuantity) {
            alert('Enter a quantity for each ingredient.');
            return;
        }
        const items = recipeIngredients.map((it) => {
            const entry = {
                inventoryItemId: Number(it.inventoryItemId),
                quantity: Number(it.quantity),
            };
            if (it.recipeId != null && it.recipeId !== '') {
                entry.recipeId = Number(it.recipeId);
            }
            return entry;
        });
        const steps = recipeSteps
            .map((st, idx) => {
                const entry = {
                    stepNumber: idx + 1,
                    instruction: String(st.instruction || '').trim(),
                };
                if (st.stepId != null && st.stepId !== '') {
                    entry.stepId = Number(st.stepId);
                }
                return entry;
            })
            .filter((st) => st.instruction);
        if (items.length === 0 || steps.length === 0) {
            alert('Add at least one valid ingredient and one step.');
            return;
        }
        const wasUpdate = isRecipeUpdate;
        try {
            setSaving(true);
            const idPayload = buildRecipeIdPayload(menuTypeFilter, menuItemId);
            const data = wasUpdate
                ? await updateFoodItemRecipe({ ...idPayload, items, steps })
                : await createFoodItemRecipe({
                      ...idPayload,
                      restaurantId: effectiveRestaurantId,
                      createdById,
                      items,
                      steps,
                  });
            setRecipeResult(data);
            alert(
                data?.message ||
                    (wasUpdate ? 'Recipe updated successfully.' : 'Recipe created successfully.')
            );
            changeMode('view');
        } catch (error) {
            alert(
                getApiErrorMessage(
                    error,
                    wasUpdate ? 'Failed to update recipe.' : 'Failed to create recipe.'
                )
            );
        } finally {
            setSaving(false);
        }
    };

    const pageTitle = foodName ? `${foodName} Recipe` : 'Recipe';

    const detailBody = (
        <div className="menu menu-template food-item-recipe-page">
            <div className="restaurant-detail-panel">
                <div className="menu-template-detail-header-row recipe-page-header-row">
                    <button
                        type="button"
                        className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                        onClick={handleBack}
                        aria-label="Back to menu"
                    >
                        <FaArrowLeft aria-hidden />
                    </button>
                    <h3 className="restaurant-detail-section-title recipe-page-title">
                        {loading && !foodName ? 'Loading…' : pageTitle}
                    </h3>
                    <div className="recipe-page-header-actions">
                        {isViewMode && recipeHasContent ? (
                            <button
                                type="button"
                                className="add-button recipe-page-header-edit-btn"
                                onClick={() => changeMode('edit')}
                                aria-label="Edit recipe"
                                title="Edit recipe"
                            >
                                <FaEdit aria-hidden />
                            </button>
                        ) : null}
                        {!loading && !loadError && !isViewMode ? (
                                <button
                                    type="submit"
                                    form="food-item-recipe-form"
                                    className="add-button recipe-page-header-btn"
                                    disabled={saving}
                                >
                                    {saving
                                        ? 'Saving…'
                                        : isRecipeUpdate
                                          ? 'Update Recipe'
                                          : 'Save Recipe'}
                                </button>
                        ) : null}
                    </div>
                </div>

                    {showServingNote ? (
                        <div className="recipe-serving-note recipe-page-serving-note" role="note">
                            <span className="recipe-serving-note-text">
                                <strong>Note:</strong>{' '}
                                {getRecipeServingNoteText(recipeMode, isRecipeUpdate)}
                            </span>
                            <button
                                type="button"
                                className="recipe-serving-note-close"
                                onClick={() => setShowServingNote(false)}
                                aria-label="Dismiss note"
                            >
                                <FontAwesomeIcon icon={faXmark} aria-hidden />
                            </button>
                        </div>
                    ) : null}

                    {loading ? (
                        <p className="order-details-loading">Loading recipe…</p>
                    ) : null}

                    {!loading && loadError ? (
                        <p className="restaurant-detail-message error">{loadError}</p>
                    ) : null}

                    {!loading && !loadError && isViewMode && recipeHasContent ? (
                        <div className="recipe-modal-body recipe-page-body">
                            <section className="recipe-modal-section recipe-modal-panel">
                                <div className="inventory-form-section-label recipe-modal-section-heading">
                                    Ingredients (for {RECIPE_SERVING_COUNT} people)
                                </div>
                                {(recipeResult.items || []).length === 0 ? (
                                    <p className="inventory-items-hint">No ingredients found.</p>
                                ) : (
                                    <ul className="recipe-ingredients-readable">
                                        {(recipeResult.items || []).map((item, idx) => {
                                            const { quantity, unit } = formatRecipeIngredientQuantity(
                                                item,
                                                recipeInventoryItems
                                            );
                                            const qtyLabel = unit
                                                ? `${quantity} ${unit}`
                                                : String(quantity);
                                            return (
                                                <li
                                                    key={`${item.recipeId || 'r'}-${idx}`}
                                                    className="recipe-ingredient-readable-item"
                                                >
                                                    <span className="recipe-ingredient-readable-name">
                                                        {item.inventoryItemName ??
                                                            `Item ${item.inventoryItemId ?? idx + 1}`}
                                                    </span>
                                                    <span className="recipe-ingredient-readable-qty">
                                                        {qtyLabel}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </section>
                            <section className="recipe-modal-section recipe-modal-panel">
                                <div className="inventory-form-section-label recipe-modal-section-heading">
                                    Steps
                                </div>
                                {(recipeResult.steps || []).length === 0 ? (
                                    <p className="inventory-items-hint">No steps found.</p>
                                ) : (
                                    <ol className="recipe-steps-readable">
                                        {(recipeResult.steps || []).map((step, idx) => (
                                            <li
                                                key={`${step.stepId || 's'}-${idx}`}
                                                className="recipe-step-readable-item"
                                            >
                                                <span
                                                    className="recipe-step-readable-badge"
                                                    aria-hidden
                                                >
                                                    {idx + 1}
                                                </span>
                                                <RecipeInstructionDisplay
                                                    instruction={step.instruction}
                                                />
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </section>
                        </div>
                    ) : null}

                    {!loading && !loadError && !isViewMode ? (
                        <form
                            id="food-item-recipe-form"
                            className="inventory-form recipe-modal-form recipe-page-form"
                            onSubmit={handleSaveRecipe}
                        >
                            <section className="recipe-modal-panel recipe-panel--ingredients">
                                <div className="recipe-section-header">
                                    <div className="recipe-section-title-wrap">
                                        <h4 className="recipe-modal-section-heading">Ingredients</h4>
                                        <p className="recipe-section-subtitle">
                                            Quantities for {RECIPE_SERVING_COUNT} people
                                        </p>
                                    </div>
                                    {groceryOptions.length > 0 && !showRecipeIngredientPicker ? (
                                        <button
                                            type="button"
                                            className="add-button recipe-section-add-btn"
                                            onClick={() => setShowRecipeIngredientPicker(true)}
                                            aria-expanded={showRecipeIngredientPicker}
                                        >
                                            Add ingredient
                                        </button>
                                    ) : null}
                                </div>

                                {groceryOptions.length === 0 && recipeIngredients.length === 0 ? (
                                    <p className="recipe-section-inline-hint">
                                        No inventory items available. Add stock in Inventory first.
                                    </p>
                                ) : null}

                                {showRecipeIngredientPicker && groceryOptions.length > 0 ? (
                                    <RecipeIngredientMultiPicker
                                        options={groceryOptions}
                                        onAddSelected={commitSelectedRecipeIngredients}
                                        onCancel={() => {
                                            setShowRecipeIngredientPicker(false);
                                        }}
                                        quantityPlaceholder="Qty"
                                    />
                                ) : null}

                                {recipeIngredients.length > 0 ? (
                                    <div className="recipe-ingredients-table">
                                        <div
                                            className="recipe-ingredients-table-head"
                                            aria-hidden="true"
                                        >
                                            <span>Item</span>
                                            <span>Quantity</span>
                                            <span />
                                        </div>
                                        <ul className="recipe-ingredients-readable recipe-ingredients-readable--editable">
                                            {recipeIngredients.map((item, index) => (
                                                <li
                                                    key={`ing-${item.inventoryItemId}-${index}`}
                                                    className="recipe-ingredient-readable-item recipe-ingredient-readable-item--editable"
                                                >
                                                    <span className="recipe-ingredient-readable-name">
                                                        {getRecipeInventoryItemName(
                                                            item.inventoryItemId
                                                        )}
                                                    </span>
                                                    <div className="inventory-input-with-addon recipe-ingredient-qty-with-unit">
                                                        <input
                                                            type="number"
                                                            className="add-menu-form-field recipe-ingredient-readable-qty inventory-input--has-suffix"
                                                            value={item.quantity}
                                                            onChange={(e) =>
                                                                updateRecipeIngredientQuantityAt(
                                                                    index,
                                                                    e.target.value
                                                                )
                                                            }
                                                            onBlur={() =>
                                                                normalizeRecipeIngredientQuantityAt(
                                                                    index
                                                                )
                                                            }
                                                            step="any"
                                                            min="0"
                                                            placeholder="Qty"
                                                            aria-label={`Quantity for ${getRecipeInventoryItemName(item.inventoryItemId)}`}
                                                        />
                                                        <span
                                                            className="inventory-input-addon inventory-input-addon--suffix"
                                                            aria-hidden="true"
                                                        >
                                                            {getRecipeInventoryItemUnit(
                                                                item.inventoryItemId
                                                            ) || 'unit'}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="action-button delete-button recipe-modal-action-btn"
                                                        title="Remove ingredient"
                                                        aria-label="Remove ingredient"
                                                        onClick={() =>
                                                            removeRecipeIngredientAt(index)
                                                        }
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}
                            </section>

                            <section className="recipe-modal-panel recipe-panel--steps">
                                <div className="recipe-section-header">
                                    <h4 className="recipe-modal-section-heading">Steps</h4>
                                    {!showRecipeStepPicker ? (
                                        <button
                                            type="button"
                                            className="add-button recipe-page-header-btn recipe-steps-header-add-btn"
                                            onClick={openRecipeStepForm}
                                        >
                                            Add step
                                        </button>
                                    ) : null}
                                </div>
                                {showRecipeStepPicker ? (
                                    <div className="recipe-step-add-row">
                                        <div className="inventory-form-group recipe-step-add-field">
                                            <label htmlFor="recipe-step-instruction">Instruction</label>
                                            <textarea
                                                id="recipe-step-instruction"
                                                className="add-menu-form-field recipe-step-textarea"
                                                rows={3}
                                                value={recipeStepDraft.instruction}
                                                onChange={(e) =>
                                                    setRecipeStepDraft((d) => ({
                                                        ...d,
                                                        instruction: e.target.value,
                                                    }))
                                                }
                                                placeholder="Describe this cooking step…"
                                            />
                                        </div>
                                        <div className="recipe-step-form-actions">
                                            <button
                                                type="button"
                                                className="close-button recipe-step-form-cancel-btn"
                                                onClick={closeRecipeStepForm}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="add-button recipe-page-header-btn"
                                                onClick={commitRecipeStep}
                                            >
                                                {editingRecipeStepIndex !== null
                                                    ? 'Update step'
                                                    : 'Add step'}
                                            </button>
                                        </div>
                                    </div>
                                ) : null}

                                {recipeSteps.length > 0 ? (
                                    <div className="recipe-steps-list recipe-steps-list--editable">
                                        {recipeSteps.map((step, index) => (
                                            <div
                                                key={`step-${index}`}
                                                className="recipe-step-card"
                                            >
                                                <div className="recipe-step-card-content">
                                                    <span
                                                        className="recipe-step-readable-badge"
                                                        aria-hidden
                                                    >
                                                        {index + 1}
                                                    </span>
                                                    <RecipeInstructionDisplay
                                                        instruction={step.instruction}
                                                    />
                                                </div>
                                                <div className="recipe-modal-list-item-actions">
                                                    <button
                                                        type="button"
                                                        className="action-button recipe-modal-action-btn"
                                                        title="Edit step"
                                                        aria-label="Edit step"
                                                        onClick={() => startEditRecipeStepAt(index)}
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-button delete-button recipe-modal-action-btn"
                                                        title="Remove step"
                                                        aria-label="Remove step"
                                                        onClick={() => removeRecipeStepAt(index)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </section>
                        </form>
                    ) : null}
            </div>
        </div>
    );

    if (embedded) {
        return detailBody;
    }

    return (
        <div className="home-container food-item-recipe-page-shell">
            <div className="tab-content">{detailBody}</div>
        </div>
    );
};

function FoodItemRecipePage(props) {
    const { foodItemId: foodItemIdProp, embedded = false } = props;
    const { foodItemId: routeFoodItemId } = useParams();
    const [searchParams] = useSearchParams();

    const foodItemId = foodItemIdProp ?? routeFoodItemId;
    const menuTypeFilter =
        searchParams.get('menuType') === 'catering' ? 'catering' : 'online';

    if (!embedded && routeFoodItemId && foodItemIdProp == null) {
        return <FoodItemRecipeRouteRedirect foodItemId={routeFoodItemId} />;
    }

    return (
        <FoodItemRecipePageView
            {...props}
            foodItemId={foodItemId}
            menuTypeFilter={props.menuTypeFilter ?? menuTypeFilter}
        />
    );
}

export { RECIPE_TAB_PREFIX, recipeTabPath };
export default FoodItemRecipePage;
